import { c as _c } from "react/compiler-runtime";
import React, { type ComponentProps } from 'react';
import { Alert, Button, CloseButton, Loader, ActionIcon, Modal, ModalProps, Tooltip, type ButtonProps } from '@mantine/core';
import { HiPlusCircle, HiRefresh, HiX } from 'react-icons/hi';
import { useRouter } from 'next/router';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useFocusTrap, useMediaQuery } from '@mantine/hooks';
import { doTo, okOrThrow } from 'shared/promise';
import { compactObjDeep, isNullOrEmpty, notNullOrEmpty } from 'shared/collection';
import { pick } from 'remeda';
import { usePortal } from 'components/portal/portal';
import { QuestionCfg, questionCfgForNew, zQuestionCfgShape } from 'shared/forecast/question';
import { AutoQ as A } from 'api/autoQ';
import { sb } from 'utils/db';
import { useUIItem, useUIState } from 'components/hooks/useUIState';
import { QuestionTitleForm, useQuestionTitleForm } from './QuestionTitle';
import { ForecastTypeCombo } from './Subqs';
import { CardCollapsable } from 'components/Card';
import { QuestionStatus } from 'shared/forecast/questionStatus';
import { KbdCombo } from 'components/Keyboard';
import { dbFormSubset } from 'components/hooks/useDbForm';
import { useQueryStringOpened } from 'components/hooks/useQueryString';
import { useDebounceMutation } from 'components/hooks/useDebounceMutation';
import { twMerge } from 'tailwind-merge';
import { ComparisonSectionControlled, useComparisonNew } from './Comparison';
import { WrapIf } from 'shared/ui';
import { useCtrlEnter } from 'components/Buttons';
import { QuestionCommonForm } from './QuestionForm';
export const useNewQModal = t0 => {
  const $ = _c(19);
  const {
    key,
    onOpen
  } = t0;
  const {
    readonly
  } = usePortal();
  let t1;
  if ($[0] !== onOpen) {
    t1 = {
      onOpen
    };
    $[0] = onOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    opened,
    close,
    open,
    toggle
  } = useQueryStringOpened(`${key}-opened`, t1);
  const fullScreen = useMediaQuery("(max-width: 768px)");
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      header: {
        display: "none"
      }
    };
    t3 = {
      content: "pt-[1em] w-[calc(min(50em,90vw))] max-h-[calc(max(100vh-4em,80em))]",
      body: "h-full"
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      backgroundOpacity: 0.5,
      style: {
        backdropFilter: "blur(3px)"
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== close || $[6] !== fullScreen || $[7] !== opened) {
    t5 = {
      opened,
      onClose: close,
      styles: t2,
      classNames: t3,
      overlayProps: t4,
      size: "auto",
      keepMounted: false,
      fullScreen,
      title: "No title"
    };
    $[5] = close;
    $[6] = fullScreen;
    $[7] = opened;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = t5 satisfies ModalProps;
  let t7;
  if ($[9] !== open) {
    t7 = {
      onClick: open
    };
    $[9] = open;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = t7 satisfies ComponentProps<typeof Button<'button'>>;
  let t9;
  if ($[11] !== close || $[12] !== open || $[13] !== opened || $[14] !== readonly || $[15] !== t6 || $[16] !== t8 || $[17] !== toggle) {
    t9 = {
      modalProps: t6,
      buttonProps: t8,
      open,
      close,
      opened,
      toggle,
      readonly
    };
    $[11] = close;
    $[12] = open;
    $[13] = opened;
    $[14] = readonly;
    $[15] = t6;
    $[16] = t8;
    $[17] = toggle;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};
type UseNewQModal = ReturnType<typeof useNewQModal>;
export const zNewQuestion = doTo(zQuestionCfgShape, ({
  title,
  subTitle,
  description
}) => z.object({
  title,
  subTitle,
  description,
  ...A.questionResponseShape
}).describe('new-question'));
export type NewQuestion = z.infer<typeof zNewQuestion>;
export const NewQuestionPopover = t0 => {
  const $ = _c(16);
  const {
    buttonProps: buttonPropsCustom,
    onOpen
  } = t0;
  let t1;
  if ($[0] !== onOpen) {
    t1 = {
      key: "new-question",
      onOpen
    };
    $[0] = onOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    modalProps,
    buttonProps,
    open,
    readonly
  } = useNewQModal(t1);
  let t2;
  if ($[2] !== onOpen || $[3] !== open) {
    t2 = () => {
      open();
      onOpen?.();
    };
    $[2] = onOpen;
    $[3] = open;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== buttonProps || $[6] !== buttonPropsCustom || $[7] !== readonly || $[8] !== t2) {
    t3 = <NewQuestionButton disabled={readonly} {...buttonProps} {...buttonPropsCustom} onClick={t2} />;
    $[5] = buttonProps;
    $[6] = buttonPropsCustom;
    $[7] = readonly;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== modalProps) {
    t4 = <NewQuestionModal modalProps={modalProps} />;
    $[10] = modalProps;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== readonly || $[13] !== t3 || $[14] !== t4) {
    t5 = <WrapIf if={readonly} wrap={_temp}>{t3}{t4}</WrapIf>;
    $[12] = readonly;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export const NewQuestionButton = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <HiPlusCircle />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Button leftSection={t0} data-test-id="new-question" children="New question" {...props} />;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export const NewQuestionModal = t0 => {
  const $ = _c(5);
  const {
    modalProps
  } = t0;
  let t1;
  if ($[0] !== modalProps.onClose) {
    t1 = <NewQuestionForm close={modalProps.onClose} />;
    $[0] = modalProps.onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== modalProps || $[3] !== t1) {
    t2 = <Modal {...modalProps}>{t1}</Modal>;
    $[2] = modalProps;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const ctrlEnter = {
  key: 'Enter',
  mods: ['Meta']
} satisfies KbdCombo;
const zRootNewQuestionForm = z.object({
  question: zNewQuestion,
  forRequest: A.zReqQuestion.shape.priorRequest
}).describe('new-question');
const initial = zRootNewQuestionForm.parse({
  question: {},
  forRequest: undefined
});
const NewQuestionForm = t0 => {
  const $ = _c(11);
  const {
    close: closeRaw
  } = t0;
  const {
    workspace_id,
    profile
  } = usePortal();
  const router = useRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      mode: "user",
      path: ["question-new"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const uiState = useUIState(t1);
  let t2;
  if ($[1] !== uiState) {
    t2 = {
      state: uiState,
      schema: zRootNewQuestionForm,
      initial
    };
    $[1] = uiState;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const rootForm = useUIItem(t2);
  let t3;
  if ($[3] !== rootForm.values.forRequest || $[4] !== workspace_id) {
    t3 = t4 => {
      const {
        question,
        options
      } = t4;
      return A.zReqQuestion.parse({
        type: "question",
        question: pick(question, ["title", "subTitle", "description"]),
        priorRequest: rootForm.values?.forRequest,
        options: {
          workspace_id,
          ...options
        }
      } satisfies A.GetAutoQRequest);
    };
    $[3] = rootForm.values.forRequest;
    $[4] = workspace_id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const makeReq = t3;
  const qForm = dbFormSubset(rootForm, "question", {
    onChange: t4 => {
      const {
        delta,
        cfg
      } = t4;
      if (!("title" in delta || "subTitle" in delta || "description" in delta)) {
        return;
      }
      if (isNullOrEmpty(cfg.title?.trim())) {
        return;
      }
      autoMutation.debouncedMutate(makeReq({
        question: cfg
      }));
    }
  });
  const retry = () => autoMutation.mutate(makeReq({
    question: qForm.values,
    options: {
      forceRefresh: true
    }
  }));
  const autoMutation = useDebounceMutation({
    mutationFn: _temp2,
    onSuccess: t5 => {
      const {
        forRequest,
        question: question_1,
        usePrior: usePrior_0
      } = t5;
      if (!question_1 || usePrior_0 && !isAutoDefault) {
        return;
      }
      rootForm.setValues({
        forRequest: compactObjDeep(forRequest),
        ...qForm.deltaWithFullPath(question_1)
      }, "auto");
    }
  });
  const getQuestion = withDefaults => {
    const values = withDefaults ? {
      ...qForm.values,
      status: "question-review" as QuestionStatus
    } : pick(qForm.values, ["title", "subTitle", "description"]);
    return {
      ...questionCfgForNew,
      ...values
    } satisfies QuestionCfg;
  };
  const createQuestionMute = useMutation({
    mutationFn: async withDefaults_0 => {
      const hasErrors = rootForm.validate();
      if (notNullOrEmpty(hasErrors)) {
        return;
      }
      const {
        data,
        error
      } = await sb.from("question").insert({
        created_by: profile.id,
        workspace_id,
        cfg: getQuestion(withDefaults_0)
      }).select("id").single();
      if (error) {
        throw error;
      }
      if (!data) {
        throw new Error("No data returned from insert");
      }
      await rootForm.del();
      return data.id;
    },
    onSuccess: questionId => {
      if (questionId) {
        router.push(`/question/${questionId}`);
        close();
      }
    },
    onError: _temp3
  });
  const isAutoDefault = isNullOrEmpty(A.zQuestionResponse.shape.question.parse(rootForm.values.question));
  const question_2 = getQuestion(true);
  const close = () => {
    rootForm.del();
    uTitleForm.reset();
    comparisonProps.clearUnselected();
    closeRaw();
  };
  const uTitleForm = useQuestionTitleForm({
    readonly: false,
    qForm,
    typeLabel: "question",
    uiState
  });
  const titlePopupFocusRef = useFocusTrap();
  const comparisonProps = useComparisonNew({
    qForm,
    comparisonKey: {
      type: question_2.forecastType,
      amountType: question_2.amountType ?? "number",
      code: question_2.forecastType
    }
  });
  let t6;
  if ($[6] !== createQuestionMute) {
    t6 = {
      onClick: () => createQuestionMute.mutate(true)
    };
    $[6] = createQuestionMute;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const ctrlEnter = useCtrlEnter(t6);
  let t7;
  if ($[8] !== createQuestionMute.error || $[9] !== createQuestionMute.isError) {
    t7 = createQuestionMute.isError && <Alert color="red">{createQuestionMute.error.message}</Alert>;
    $[8] = createQuestionMute.error;
    $[9] = createQuestionMute.isError;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return <div className="flex flex-col" onKeyDown={ctrlEnter.onKeyDown} ref={titlePopupFocusRef}><QuestionTitleForm uTitleForm={uTitleForm} topRight={<CloseButton onClick={close} />} belowForm={<div className="flex items-center gap-4 mt-2">{t7}{notNullOrEmpty(question_2.title) && <Button className="ml-auto" variant="outline" loading={createQuestionMute.isPending} onClick={() => createQuestionMute.mutate(false)}>Create (as is)</Button>}</div>} />{(autoMutation.isPending || autoMutation.isError || !isAutoDefault) && <AutoConfigureCard mutation={autoMutation} retry={retry}>{!isAutoDefault && <div className="space-y-4"><ForecastTypeCombo {...qForm.getInputProps("forecastType")} showLabel={true} withinPortal={false} /><QuestionCommonForm form={qForm} simplified={true} /><ComparisonSectionControlled uComparison={comparisonProps} mode="new" /><div className="flex items-center justify-end pt-4"><Button color="blue" loading={createQuestionMute.isPending} disabled={createQuestionMute.isPending} {...ctrlEnter.buttonProps}>Create</Button></div></div>}</AutoConfigureCard>}</div>;
};
export const AutoConfigureCard = t0 => {
  const $ = _c(23);
  const {
    children,
    mutation,
    onReset,
    title: t1,
    retry,
    color: t2,
    className
  } = t0;
  const title = t1 === undefined ? "Configuration Suggestions" : t1;
  const color = t2 === undefined ? "blue" : t2;
  let t3;
  if ($[0] !== className) {
    t3 = twMerge("mt-2", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      container: "p-3"
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== color || $[4] !== mutation || $[5] !== onReset || $[6] !== title) {
    t5 = mutation.isPending ? <><Loader size="sm" color={color} className="ml-auto" /><div className="grow">{title}</div><ActionIcon onClick={e => {
        e.stopPropagation();
        mutation.reset();
        onReset?.();
      }}><HiX /></ActionIcon></> : title;
    $[3] = color;
    $[4] = mutation;
    $[5] = onReset;
    $[6] = title;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== mutation.isPending || $[9] !== retry) {
    t6 = retry && !mutation.isPending && <ActionIcon className="ml-auto" color="inherit" onClick={e_0 => {
      e_0.stopPropagation();
      retry();
    }}><HiRefresh /></ActionIcon>;
    $[8] = mutation.isPending;
    $[9] = retry;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="flex items-center gap-2 text-sm">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== mutation.error) {
    t8 = mutation.error instanceof Error && <Alert color="red">{mutation.error.message}</Alert>;
    $[14] = mutation.error;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== children || $[17] !== color || $[18] !== mutation.isPending || $[19] !== t3 || $[20] !== t7 || $[21] !== t8) {
    t9 = <CardCollapsable className={t3} readonlyOverlay={mutation.isPending} color={color} classNames={t4} title={t7}>{t8}{children}</CardCollapsable>;
    $[16] = children;
    $[17] = color;
    $[18] = mutation.isPending;
    $[19] = t3;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
function _temp(c) {
  return <Tooltip label="This workspace is read only"><div>{c}</div></Tooltip>;
}
async function _temp2(p, signal) {
  const {
    question: question_0,
    usePrior
  } = await A.getAutoQ(p, {
    signal
  }).then(okOrThrow);
  return {
    question: question_0,
    usePrior,
    forRequest: p.question
  };
}
function _temp3(err) {
  return console.error("Failed to create question:", err);
}